import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/base/Pagination";
import FilterPanel from "@/pages/list/components/FilterPanel";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { categoryGroups, getCategoryGroup, getSubCategory } from "@/mocks/categories";
import {
  filterVideos,
  getCategoryType,
  getGenreOptions,
  getRegionsOf,
  getVideosByCategoryId,
  getYearsOf,
  sortOptions,
  typeLabels,
  type SortKey,
} from "@/utils/video";

const PAGE_SIZE = 8;

export default function ListPage() {
  const { categoryId = "" } = useParams();
  const sub = getSubCategory(categoryId);
  const group = categoryGroups.find((item) => item.type === categoryId || item.slug === categoryId);
  const categoryType = getCategoryType(categoryId);
  const source = useMemo(() => getVideosByCategoryId(categoryId), [categoryId]);

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setPage(1);
  }, [categoryId]);

  useEffect(() => {
    setPage(1);
  }, [genre, region, year, sort]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const genres = getGenreOptions(source);
  const regions = getRegionsOf(source);
  const years = getYearsOf(source);
  const filtered = filterVideos(source, { genre, region, year, sort });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const title = sub?.name ?? group?.name ?? "视频列表";
  const description =
    sub?.description ??
    group?.description ??
    "按题材、地区与年份筛选，快速找到想看的内容。";

  useDocumentMeta({
    title: `${title}视频列表 - 逼特逼视频`,
    description: `${title}内容合集：${description} 共 ${source.length} 部内容，支持多维筛选与排序。`,
    keywords: `${title},${categoryType ? typeLabels[categoryType] : "视频"},在线观看,逼特逼视频`,
  });

  const hasFilter = genre !== "全部" || region !== "全部" || year !== "全部";
  const parentGroup = categoryType ? getCategoryGroup(categoryType) : undefined;

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("latest");
  };

  const handleChange = (key: "genre" | "region" | "year", value: string) => {
    if (key === "genre") setGenre(value);
    if (key === "region") setRegion(value);
    if (key === "year") setYear(value);
  };

  if (!sub && !group) {
    return (
      <div className="page-shell py-16 md:py-24">
        <EmptyState
          icon="ri-folder-unknow-line"
          title="未找到该分类"
          description="这个分类可能已经调整或下线，回到首页重新选择吧。"
        />
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-primary-400 hover:text-primary-300">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const siblings = categoryType
    ? (categoryGroups.find((item) => item.type === categoryType)?.subcategories ?? [])
    : [];

  return (
    <div className="page-shell py-7 md:py-10">
      <Breadcrumb
        items={[
          ...(parentGroup && sub
            ? [{ label: `${parentGroup.name}频道`, to: `/category/${parentGroup.type}` }]
            : []),
          { label: title },
        ]}
      />

      <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-3xl">
            {title}
            <span className="ml-2 align-middle font-body text-xs font-normal text-foreground-500">
              {categoryType ? typeLabels[categoryType] : ""}
            </span>
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{description}</p>
        </div>
        <p className="whitespace-nowrap font-label text-xs text-foreground-500">
          共 <span className="text-primary-400">{filtered.length}</span> 部
          {hasFilter ? "（已筛选）" : "内容"}
        </p>
      </div>

      {siblings.length > 0 && (
        <div className="no-scrollbar mt-5 flex gap-1.5 overflow-x-auto pb-1">
          <Link
            to={`/list/${categoryType}`}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-xs transition-colors ${
              categoryId === categoryType
                ? "bg-primary-500 text-background-50"
                : "border border-background-200 bg-background-100 text-foreground-600 hover:border-primary-400 hover:text-primary-400"
            }`}
          >
            全部
          </Link>
          {siblings.map((item) => (
            <Link
              key={item.id}
              to={`/list/${item.id}`}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-xs transition-colors ${
                categoryId === item.id
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-200 bg-background-100 text-foreground-600 hover:border-primary-400 hover:text-primary-400"
              }`}
            >
              {item.name}
            </Link>
          ))}
        </div>
      )}

      <div className="mt-7 flex flex-col gap-6 lg:flex-row lg:gap-7">
        <aside className="hidden w-[248px] shrink-0 lg:block">
          <div className="sticky top-24">
            <FilterPanel
              genres={genres}
              regions={regions}
              years={years}
              values={{ genre, region, year }}
              onChange={handleChange}
              onReset={resetFilters}
            />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`whitespace-nowrap rounded px-2.5 py-1.5 font-label text-xs transition-colors ${
                    sort === option.key
                      ? "bg-primary-500/15 text-primary-400"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between gap-2 sm:justify-end">
              <span className="whitespace-nowrap text-[11px] text-foreground-500">
                第 {currentPage} / {totalPages} 页
              </span>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-2.5 py-1.5 text-xs text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line text-sm" />
                筛选
                {hasFilter && <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />}
              </button>
            </div>
          </div>

          {hasFilter && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {[
                { key: "genre" as const, value: genre, reset: () => setGenre("全部") },
                { key: "region" as const, value: region, reset: () => setRegion("全部") },
                { key: "year" as const, value: year, reset: () => setYear("全部") },
              ]
                .filter((item) => item.value !== "全部")
                .map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={item.reset}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-primary-400/50 bg-primary-500/10 px-2.5 py-1 text-[11px] text-primary-400"
                  >
                    {item.value}
                    <i className="ri-close-line text-xs" />
                  </button>
                ))}
              <button
                type="button"
                onClick={resetFilters}
                className="whitespace-nowrap text-[11px] text-foreground-500 underline-offset-2 hover:text-primary-400 hover:underline"
              >
                清空全部
              </button>
            </div>
          )}

          {paged.length === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="未找到符合条件的视频"
              description="试着减少筛选条件，或换一个年份、地区看看。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {paged.map((video) => (
                <VideoCard key={video.id} video={video} showSummary />
              ))}
            </div>
          )}

          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-background-50/80 backdrop-blur-sm"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-xl border-t border-background-200 bg-background-50 p-4 pb-8">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-base text-foreground-900">筛选视频</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-background-200 text-foreground-700"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterPanel
              genres={genres}
              regions={regions}
              years={years}
              values={{ genre, region, year }}
              onChange={handleChange}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm text-background-50"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}